import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { authService } from "../services";
import type { Role, User } from "../services/types";
import { canAct } from "../config/roles";

interface AuthContextValue {
  user: User | null;
  role: Role;
  isViewer: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Dev-only role override used by the top-bar switcher to test role behaviour. */
  switchRole: (role: Role) => void;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const DEFAULT_ROLE: Role = "super_admin";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role>(DEFAULT_ROLE);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    authService
      .getCurrentUser()
      .then((restored) => {
        if (!active) return;
        if (restored) {
          setUser(restored);
          setRole((restored.role ? String(restored.role).toLowerCase() : DEFAULT_ROLE) as Role);
        }
      })
      .catch(() => {
        // Ignore restore errors; fall through to logged-out state.
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const session = await authService.login({ email, password });
    setUser(session.user);
    setRole((session.user.role ? String(session.user.role).toLowerCase() : DEFAULT_ROLE) as Role);
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setRole(DEFAULT_ROLE);
  }, []);

  /** Dev-only: override the current role to exercise role-gated behaviour. */
  const switchRole = useCallback((nextRole: Role) => {
    setRole(nextRole);
  }, []);

  const updateUser = useCallback((updated: User) => {
    setUser(updated);
    if (updated.role) setRole(String(updated.role).toLowerCase() as Role);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role,
      isViewer: !canAct(role),
      loading,
      login,
      logout,
      switchRole,
      updateUser,
    }),
    [user, role, loading, login, logout, switchRole, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}